import { useParams, useNavigate, Navigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Header } from "@/components/Header";
import { api } from "@/lib/api";
import { GameLeaderboard } from "@/components/Leaderboard";
import { ArrowLeft, Loader2 } from "lucide-react";
import SnakeGame from "@/components/games/SnakeGame";
import BreakoutGame from "@/components/games/BreakoutGame";
import TetrisGame from "@/components/games/TetrisGame";

const REGISTRY = {
  snake: { title: "NEON SNAKE", comp: SnakeGame },
  breakout: { title: "BRICK BUSTER", comp: BreakoutGame },
  tetris: { title: "BLOCK DROP", comp: TetrisGame },
};

export default function Play() {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState("loading");
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    api.get("/games").then((r) => {
      const g = r.data.games.find((x) => x.id === gameId);
      setState(g && g.unlocked ? "ok" : "locked");
    }).catch(() => setState("locked"));
  }, [gameId]);

  const handleGameOver = (score) => {
    api.post("/scores", { game_id: gameId, score })
      .then(({ data }) => {
        if (data.is_new_best) toast.success(`New personal best: ${data.best}! 🏆`);
        setRefreshKey((k) => k + 1);
      })
      .catch(() => {});
  };

  const entry = REGISTRY[gameId];
  if (!entry) return <Navigate to="/games" replace />;

  const GameComp = entry.comp;

  return (
    <div className="min-h-screen bg-[#0A0A14]">
      <Header />
      <div className="max-w-5xl mx-auto px-6 py-10">
        <button onClick={() => navigate("/games")} data-testid="back-to-library" className="flex items-center gap-2 text-slate-400 hover:text-[#00F0FF] transition-colors duration-200 mb-6 font-arcade text-lg">
          <ArrowLeft className="w-4 h-4" /> BACK TO VAULT
        </button>
        <h1 className="font-head font-900 text-3xl sm:text-4xl text-[#00F0FF] mb-8">{entry.title}</h1>

        {state === "loading" && <div className="flex items-center gap-3 text-slate-400 font-arcade text-xl"><Loader2 className="w-6 h-6 animate-spin" /> LOADING…</div>}
        {state === "locked" && (
          <div className="bg-[#11111A] border border-[#FF006E]/40 p-8" data-testid="locked-notice">
            <p className="font-arcade text-2xl text-[#FF006E] mb-2">GAME LOCKED</p>
            <p className="text-slate-400 mb-6">Your current plan doesn't include this game. Upgrade with BTC to play.</p>
            <button onClick={() => navigate("/games")} className="font-arcade text-lg px-6 py-2 bg-[#F5C842] text-black border border-[#F5C842] hover:shadow-[3px_3px_0px_#FF006E] transition-transform duration-200">BACK TO VAULT</button>
          </div>
        )}
        {state === "ok" && (
          <div className="grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 bg-[#11111A] border border-cyan-500/20 p-4 sm:p-8" data-testid="game-canvas-wrap">
              <GameComp onGameOver={handleGameOver} />
            </div>
            <GameLeaderboard gameId={gameId} refreshKey={refreshKey} />
          </div>
        )}
      </div>
    </div>
  );
}
